import { useState, useEffect } from "react"
import axios from "axios";
import { auth } from "@/pages/LoginPage";
import { API_BASE_URL } from "@/lib/api";

export default function SessionsPage() {
  const [formData, setFormData] = useState({
    name: "",
    date: "",
    time: ""
  });
  const [sessions, setSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Booking form states
  const [singleBookingData, setSingleBookingData] = useState({
    number: "",
    qty: "",
    amount: "",
    sessionId: ""
  });
  
  const [rajshreeBookingData, setRajshreeBookingData] = useState({
    series: "",
    number: "",
    qty: "",
    amount: "",
    sessionId: ""
  });
  
  const [choiceBookingData, setChoiceBookingData] = useState({
    numbers: "",
    qty: "",
    amount: "",
    sessionId: ""
  });

  const [creatingSingle, setCreatingSingle] = useState(false);
  const [creatingRajshree, setCreatingRajshree] = useState(false);
  const [creatingChoice, setCreatingChoice] = useState(false);
  const [conductingDraw, setConductingDraw] = useState(false);
  
  const [selectedSession, setSelectedSession] = useState<any>(null);
  const [activeBookingType, setActiveBookingType] = useState<'single' | 'rajshree' | 'choice'>('single');
  
  // Session details state
  const [sessionDetails, setSessionDetails] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'singles' | 'rajshree' | 'choices' | 'results'>('singles');
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Draw results state
  const [drawResults, setDrawResults] = useState<any>(null);
  const [showResultsModal, setShowResultsModal] = useState(false);

  // Fetch sessions on component mount
  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const token = auth.getToken();
      if (!token) {
        alert("Please login first");
        return;
      }

      const response = await axios.get(
        `${API_BASE_URL}/sessions/list`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setSessions(response.data.sessions || []);
      } else {
        console.error("Failed to load sessions:", response.data.error);
      }
    } catch (err: any) {
      console.error("Error loading sessions:", err);
      alert(err.response?.data?.error || "Failed to load sessions");
    } finally {
      setLoading(false);
    }
  };

  // Load session details
  const loadSessionDetails = async (sessionId: string) => {
    setDetailsLoading(true);
    try {
      const token = auth.getToken();
      if (!token) return;

      // Load singles
      const singlesResponse = await axios.get(
        `${API_BASE_URL}/single/list?sessionId=${sessionId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Load rajshree
      let rajshrees = [];
      try {
        const rajshreeResponse = await axios.get(
          `${API_BASE_URL}/rajshree/list?sessionId=${sessionId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        rajshrees = rajshreeResponse.data.rajshrees || [];
      } catch (error) {
        console.log("Rajshree endpoint error:", error);
      }

      // Load choices
      let choices = [];
      try {
        const choicesResponse = await axios.get(
          `${API_BASE_URL}/choice/list?sessionId=${sessionId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        choices = choicesResponse.data.choices || [];
      } catch (error) {
        console.log("Choices endpoint error:", error);
      }

      // Load results if they exist
      let results = [];
      try {
        const resultsResponse = await axios.get(
          `${API_BASE_URL}/sessions/list`, // This endpoint already includes results
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const sessionWithResults = resultsResponse.data.sessions.find((s: any) => s.id === sessionId);
        results = sessionWithResults?.results || [];
      } catch (error) {
        console.log("Results endpoint error:", error);
      }

      setSessionDetails({
        singles: singlesResponse.data.singles || [],
        rajshrees,
        choices,
        results
      });

    } catch (err: any) {
      console.error("Error loading session details:", err);
      alert(err.response?.data?.error || "Failed to load session details");
    } finally {
      setDetailsLoading(false);
    }
  };

  // Conduct draw function
  const conductDraw = async (session: any) => {
    if (!session.active) {
      alert("This session is already closed. Cannot conduct draw.");
      return;
    }

    if (!window.confirm(`Are you sure you want to conduct the draw for "${session.name}"? This will generate winning numbers, calculate winnings, and close the session. This action cannot be undone.`)) {
      return;
    }

    setConductingDraw(true);
    try {
      const token = auth.getToken();
      if (!token) {
        alert("Please login first");
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/sessions/draw-results`,
        {
          sessionId: session.id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        setDrawResults(response.data.drawResults);
        setShowResultsModal(true);
        alert("Draw completed successfully! Winnings have been distributed.");
        loadSessions(); // Refresh the sessions list
      } else {
        alert(response.data.error || "Failed to conduct draw");
      }
    } catch (err: any) {
      console.error("Draw error:", err);
      alert(err.response?.data?.error || "Failed to conduct draw");
    } finally {
      setConductingDraw(false);
    }
  };

  const openSessionDetails = async (session: any) => {
    setSelectedSession(session);
    await loadSessionDetails(session.id);
  };

  const closeSessionDetails = () => {
    setSelectedSession(null);
    setSessionDetails(null);
    setActiveTab('singles');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.date || !formData.time) {
      alert("Please fill all fields");
      return;
    }

    setSubmitting(true);
    try {
      const token = auth.getToken();
      if (!token) {
        alert("Please login first");
        return;
      }

      const drawTime = `${formData.date} ${formData.time}`;
      
      const response = await axios.post(
        `${API_BASE_URL}/sessions/create`,
        {
          name: formData.name,
          drawTime: drawTime,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        setFormData({ name: "", date: "", time: "" });
        loadSessions();
        alert("Session created successfully!");
      } else {
        alert(response.data.error || "Failed to create session");
      }
    } catch (err: any) {
      console.error("Submission error:", err);
      alert(err.response?.data?.error || "Failed to create session");
    } finally {
      setSubmitting(false);
    }
  };

  // Booking modal functions
  const openBookingModal = (session: any, type: 'single' | 'rajshree' | 'choice') => {
    setSelectedSession(session);
    setActiveBookingType(type);
    
    // Reset form data based on type
    if (type === 'single') {
      setSingleBookingData({
        number: "",
        qty: "",
        amount: "",
        sessionId: session.id
      });
    } else if (type === 'rajshree') {
      setRajshreeBookingData({
        series: "",
        number: "",
        qty: "",
        amount: "",
        sessionId: session.id
      });
    } else if (type === 'choice') {
      setChoiceBookingData({
        numbers: "",
        qty: "",
        amount: "",
        sessionId: session.id
      });
    }
  };

  const closeBookingModal = () => {
    setSelectedSession(null);
    setActiveBookingType('single');
  };

  // Form change handlers
  const handleSingleBookingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSingleBookingData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRajshreeBookingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setRajshreeBookingData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleChoiceBookingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setChoiceBookingData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Create booking functions
  const createSingleBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!singleBookingData.number || !singleBookingData.qty || !singleBookingData.amount) {
      alert("Please fill all fields");
      return;
    }

    setCreatingSingle(true);
    try {
      const token = auth.getToken();
      if (!token) {
        alert("Please login first");
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/single/create`,
        {
          number: parseInt(singleBookingData.number),
          qty: parseInt(singleBookingData.qty),
          amount: parseFloat(singleBookingData.amount),
          sessionId: singleBookingData.sessionId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        alert("Single booking created successfully!");
        closeBookingModal();
        loadSessions();
        if (sessionDetails) {
          await loadSessionDetails(singleBookingData.sessionId);
        }
      } else {
        alert(response.data.error || "Failed to create single booking");
      }
    } catch (err: any) {
      console.error("Single booking error:", err);
      alert(err.response?.data?.error || "Failed to create single booking");
    } finally {
      setCreatingSingle(false);
    }
  };

  const createRajshreeBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!rajshreeBookingData.series || !rajshreeBookingData.number || !rajshreeBookingData.qty || !rajshreeBookingData.amount) {
      alert("Please fill all fields");
      return;
    }

    setCreatingRajshree(true);
    try {
      const token = auth.getToken();
      if (!token) {
        alert("Please login first");
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/rajshree/create`,
        {
          series: rajshreeBookingData.series,
          number: parseInt(rajshreeBookingData.number),
          qty: parseInt(rajshreeBookingData.qty),
          amount: parseFloat(rajshreeBookingData.amount),
          sessionId: rajshreeBookingData.sessionId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        alert("Rajshree booking created successfully!");
        closeBookingModal();
        loadSessions();
        if (sessionDetails) {
          await loadSessionDetails(rajshreeBookingData.sessionId);
        }
      } else {
        alert(response.data.error || "Failed to create rajshree booking");
      }
    } catch (err: any) {
      console.error("Rajshree booking error:", err);
      alert(err.response?.data?.error || "Failed to create rajshree booking");
    } finally {
      setCreatingRajshree(false);
    }
  };

  const createChoiceBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!choiceBookingData.numbers || !choiceBookingData.qty || !choiceBookingData.amount) {
      alert("Please fill all fields");
      return;
    }

    setCreatingChoice(true);
    try {
      const token = auth.getToken();
      if (!token) {
        alert("Please login first");
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/choice/create`,
        {
          numbers: choiceBookingData.numbers,
          qty: parseInt(choiceBookingData.qty),
          amount: parseFloat(choiceBookingData.amount),
          sessionId: choiceBookingData.sessionId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        alert("Choice booking created successfully!");
        closeBookingModal();
        loadSessions();
        if (sessionDetails) {
          await loadSessionDetails(choiceBookingData.sessionId);
        }
      } else {
        alert(response.data.error || "Failed to create choice booking");
      }
    } catch (err: any) {
      console.error("Choice booking error:", err);
      alert(err.response?.data?.error || "Failed to create choice booking");
    } finally {
      setCreatingChoice(false);
    }
  };

  const formatDateTime = (dateTimeString: string) => {
    try {
      return new Date(dateTimeString).toLocaleString();
    } catch (error) {
      return dateTimeString;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Create Session Card */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-4">Create Lottery Session</h2>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Session Name</label>
            <input 
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Morning Draw"
              className="w-full p-2 border rounded"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Select Date</label>
            <input 
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className="w-full p-2 border rounded"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Draw Time</label>
            <input 
              type="time"
              name="time"
              value={formData.time}
              onChange={handleChange}
              className="w-full p-2 border rounded"
              required
            />
          </div>

          <div className="flex items-end">
            <button 
              type="submit" 
              disabled={submitting}
              className="w-full bg-blue-600 text-white p-2 rounded hover:bg-blue-700 disabled:bg-gray-400"
            >
              {submitting ? "Creating..." : "Create Session"}
            </button>
          </div>
        </form>
      </div>

      {/* Sessions List Card */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Sessions List</h2>
          <button 
            onClick={loadSessions}
            disabled={loading}
            className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 disabled:bg-gray-400"
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>

        {loading ? (
          <div className="text-center py-4">Loading sessions...</div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-4 text-gray-500">No sessions found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead>
                <tr className="bg-gray-100">
                  <th className="py-3 px-4 border-b text-left">Name</th>
                  <th className="py-3 px-4 border-b text-left">Draw Time</th>
                  <th className="py-3 px-4 border-b text-left">Status</th>
                  <th className="py-3 px-4 border-b text-center">Single Bets</th>
                  <th className="py-3 px-4 border-b text-center">Rajshree Bets</th>
                  <th className="py-3 px-4 border-b text-center">Choice Bets</th>
                  <th className="py-3 px-4 border-b text-center">Results</th>
                  <th className="py-3 px-4 border-b text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((session) => (
                  <tr key={session.id} className="hover:bg-gray-50">
                    <td className="py-3 px-4 border-b font-medium">{session.name}</td>
                    <td className="py-3 px-4 border-b">{formatDateTime(session.drawTime)}</td>
                    <td className="py-3 px-4 border-b">
                      <span className={`px-2 py-1 rounded text-xs ${
                        session.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {session.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-3 px-4 border-b text-center">
                      <span className={`font-medium ${
                        session.stats.singleBets > 0 ? 'text-green-600' : 'text-gray-500'
                      }`}>
                        {session.stats.singleBets}
                      </span>
                    </td>
                    <td className="py-3 px-4 border-b text-center">
                      <span className={`font-medium ${
                        session.stats.rajshreeBets > 0 ? 'text-blue-600' : 'text-gray-500'
                      }`}>
                        {session.stats.rajshreeBets}
                      </span>
                    </td>
                    <td className="py-3 px-4 border-b text-center">
                      <span className={`font-medium ${
                        session.stats.choiceBets > 0 ? 'text-purple-600' : 'text-gray-500'
                      }`}>
                        {session.stats.choiceBets}
                      </span>
                    </td>
                    <td className="py-3 px-4 border-b text-center">
                      <span className={`font-medium ${
                        session.stats.results > 0 ? 'text-orange-600' : 'text-gray-500'
                      }`}>
                        {session.stats.results}
                      </span>
                    </td>
                    <td className="py-3 px-4 border-b text-center">
                      <div className="flex flex-col space-y-2">
                        <div className="flex justify-center space-x-2">
                          <button
                            onClick={() => openBookingModal(session, 'single')}
                            className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                            disabled={!session.active}
                          >
                            Add Single
                          </button>
                          <button
                            onClick={() => openBookingModal(session, 'rajshree')}
                            className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                            disabled={!session.active}
                          >
                            Add Rajshree
                          </button>
                          <button
                            onClick={() => openBookingModal(session, 'choice')}
                            className="bg-purple-600 text-white px-3 py-1 rounded text-sm hover:bg-purple-700"
                            disabled={!session.active}
                          >
                            Add Choice
                          </button>
                        </div>
                        <div className="flex justify-center space-x-2">
                          <button
                            onClick={() => openSessionDetails(session)}
                            className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700"
                          >
                            View Details
                          </button>
                          {session.active && (
                            <button
                              onClick={() => conductDraw(session)}
                              disabled={conductingDraw}
                              className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 disabled:bg-gray-400"
                            >
                              {conductingDraw ? "Drawing..." : "Conduct Draw"}
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Booking Modal */}
      {selectedSession && !sessionDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {activeBookingType === 'single' && `Create Single Booking - ${selectedSession.name}`}
                {activeBookingType === 'rajshree' && `Create Rajshree Booking - ${selectedSession.name}`}
                {activeBookingType === 'choice' && `Create Choice Booking - ${selectedSession.name}`}
              </h3>
              <button
                onClick={closeBookingModal}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            {/* Single Booking Form */}
            {activeBookingType === 'single' && (
              <form onSubmit={createSingleBooking} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Number (0-999)</label>
                  <input 
                    type="number"
                    name="number"
                    value={singleBookingData.number}
                    onChange={handleSingleBookingChange}
                    placeholder="Enter number"
                    className="w-full p-2 border rounded"
                    min="0"
                    max="999"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Quantity</label>
                  <input 
                    type="number"
                    name="qty"
                    value={singleBookingData.qty}
                    onChange={handleSingleBookingChange}
                    placeholder="Enter quantity"
                    className="w-full p-2 border rounded"
                    min="1"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Amount</label>
                  <input 
                    type="number"
                    name="amount"
                    value={singleBookingData.amount}
                    onChange={handleSingleBookingChange}
                    placeholder="Enter amount"
                    className="w-full p-2 border rounded"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeBookingModal}
                    className="flex-1 bg-gray-500 text-white p-2 rounded hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    disabled={creatingSingle}
                    className="flex-1 bg-green-600 text-white p-2 rounded hover:bg-green-700 disabled:bg-gray-400"
                  >
                    {creatingSingle ? "Creating..." : "Create Booking"}
                  </button>
                </div>
              </form>
            )}

            {/* Rajshree Booking Form */}
            {activeBookingType === 'rajshree' && (
              <form onSubmit={createRajshreeBooking} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Series</label>
                  <input 
                    type="text"
                    name="series"
                    value={rajshreeBookingData.series}
                    onChange={handleRajshreeBookingChange}
                    placeholder="Enter series"
                    className="w-full p-2 border rounded"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Number</label>
                  <input 
                    type="number"
                    name="number"
                    value={rajshreeBookingData.number}
                    onChange={handleRajshreeBookingChange}
                    placeholder="Enter number"
                    className="w-full p-2 border rounded"
                    min="0"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Quantity</label>
                  <input 
                    type="number"
                    name="qty"
                    value={rajshreeBookingData.qty}
                    onChange={handleRajshreeBookingChange}
                    placeholder="Enter quantity"
                    className="w-full p-2 border rounded"
                    min="1"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Amount</label>
                  <input 
                    type="number"
                    name="amount"
                    value={rajshreeBookingData.amount}
                    onChange={handleRajshreeBookingChange}
                    placeholder="Enter amount"
                    className="w-full p-2 border rounded"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeBookingModal}
                    className="flex-1 bg-gray-500 text-white p-2 rounded hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    disabled={creatingRajshree}
                    className="flex-1 bg-blue-600 text-white p-2 rounded hover:bg-blue-700 disabled:bg-gray-400"
                  >
                    {creatingRajshree ? "Creating..." : "Create Booking"}
                  </button>
                </div>
              </form>
            )}

            {/* Choice Booking Form */}
            {activeBookingType === 'choice' && (
              <form onSubmit={createChoiceBooking} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Numbers</label>
                  <input 
                    type="text"
                    name="numbers"
                    value={choiceBookingData.numbers}
                    onChange={handleChoiceBookingChange}
                    placeholder="Enter numbers (comma separated)"
                    className="w-full p-2 border rounded"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Quantity</label>
                  <input 
                    type="number"
                    name="qty"
                    value={choiceBookingData.qty}
                    onChange={handleChoiceBookingChange}
                    placeholder="Enter quantity"
                    className="w-full p-2 border rounded"
                    min="1"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Amount</label>
                  <input 
                    type="number"
                    name="amount"
                    value={choiceBookingData.amount}
                    onChange={handleChoiceBookingChange}
                    placeholder="Enter amount"
                    className="w-full p-2 border rounded"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeBookingModal}
                    className="flex-1 bg-gray-500 text-white p-2 rounded hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    disabled={creatingChoice}
                    className="flex-1 bg-purple-600 text-white p-2 rounded hover:bg-purple-700 disabled:bg-gray-400"
                  >
                    {creatingChoice ? "Creating..." : "Create Booking"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Session Details Modal */}
      {selectedSession && sessionDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-xl font-semibold">
                  {selectedSession.name} - Details
                </h3>
                <p className="text-gray-600">
                  Draw Time: {formatDateTime(selectedSession.drawTime)}
                </p>
                <p className={`text-sm ${selectedSession.active ? 'text-green-600' : 'text-red-600'}`}>
                  Status: {selectedSession.active ? 'Active' : 'Closed'}
                </p>
              </div>
              <div className="flex space-x-2">
                {selectedSession.active && (
                  <button
                    onClick={() => conductDraw(selectedSession)}
                    disabled={conductingDraw}
                    className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:bg-gray-400"
                  >
                    {conductingDraw ? "Drawing..." : "Conduct Draw"}
                  </button>
                )}
                <button
                  onClick={closeSessionDetails}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b mb-6">
              <nav className="flex space-x-8">
                <button
                  onClick={() => setActiveTab('singles')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'singles'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Single Bets ({sessionDetails.singles.length})
                </button>
                <button
                  onClick={() => setActiveTab('rajshree')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'rajshree'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Rajshree Bets ({sessionDetails.rajshrees.length})
                </button>
                <button
                  onClick={() => setActiveTab('choices')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'choices'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Choice Bets ({sessionDetails.choices.length})
                </button>
                <button
                  onClick={() => setActiveTab('results')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'results'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Results ({sessionDetails.results.length})
                </button>
              </nav>
            </div>

            {detailsLoading ? (
              <div className="text-center py-8">Loading details...</div>
            ) : (
              <>
                {/* Singles Tab */}
                {activeTab === 'singles' && (
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="text-lg font-medium">Single Bets</h4>
                      {selectedSession.active && (
                        <button
                          onClick={() => openBookingModal(selectedSession, 'single')}
                          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                        >
                          Add Single Bet
                        </button>
                      )}
                    </div>
                    {sessionDetails.singles.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        No single bets found for this session
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full bg-white">
                          <thead>
                            <tr className="bg-gray-50">
                              <th className="py-2 px-4 border-b text-left">User ID</th>
                              <th className="py-2 px-4 border-b text-left">Number</th>
                              <th className="py-2 px-4 border-b text-right">Quantity</th>
                              <th className="py-2 px-4 border-b text-right">Amount</th>
                              <th className="py-2 px-4 border-b text-right">Total</th>
                              <th className="py-2 px-4 border-b text-left">Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sessionDetails.singles.map((single: any) => (
                              <tr key={single.id} className="hover:bg-gray-50">
                                <td className="py-2 px-4 border-b">
                                  {single.user?.userId || 'N/A'}
                                </td>
                                <td className="py-2 px-4 border-b font-mono">
                                  {single.number.toString().padStart(3, '0')}
                                </td>
                                <td className="py-2 px-4 border-b text-right">
                                  {single.qty}
                                </td>
                                <td className="py-2 px-4 border-b text-right">
                                  {formatCurrency(single.amount)}
                                </td>
                                <td className="py-2 px-4 border-b text-right font-medium">
                                  {formatCurrency(single.totalAmount)}
                                </td>
                                <td className="py-2 px-4 border-b text-sm text-gray-500">
                                  {new Date(single.createdAt).toLocaleString()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Rajshree Tab */}
                {activeTab === 'rajshree' && (
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="text-lg font-medium">Rajshree Bets</h4>
                      {selectedSession.active && (
                        <button
                          onClick={() => openBookingModal(selectedSession, 'rajshree')}
                          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                        >
                          Add Rajshree Bet
                        </button>
                      )}
                    </div>
                    {sessionDetails.rajshrees.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        No rajshree bets found for this session
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full bg-white">
                          <thead>
                            <tr className="bg-gray-50">
                              <th className="py-2 px-4 border-b text-left">User ID</th>
                              <th className="py-2 px-4 border-b text-left">Series</th>
                              <th className="py-2 px-4 border-b text-left">Number</th>
                              <th className="py-2 px-4 border-b text-right">Quantity</th>
                              <th className="py-2 px-4 border-b text-right">Amount</th>
                              <th className="py-2 px-4 border-b text-right">Total</th>
                              <th className="py-2 px-4 border-b text-left">Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sessionDetails.rajshrees.map((rajshree: any) => (
                              <tr key={rajshree.id} className="hover:bg-gray-50">
                                <td className="py-2 px-4 border-b">
                                  {rajshree.user?.userId || 'N/A'}
                                </td>
                                <td className="py-2 px-4 border-b">
                                  {rajshree.series}
                                </td>
                                <td className="py-2 px-4 border-b font-mono">
                                  {rajshree.number}
                                </td>
                                <td className="py-2 px-4 border-b text-right">
                                  {rajshree.qty}
                                </td>
                                <td className="py-2 px-4 border-b text-right">
                                  {formatCurrency(rajshree.amount)}
                                </td>
                                <td className="py-2 px-4 border-b text-right font-medium">
                                  {formatCurrency(rajshree.totalAmount)}
                                </td>
                                <td className="py-2 px-4 border-b text-sm text-gray-500">
                                  {new Date(rajshree.createdAt).toLocaleString()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Choices Tab */}
                {activeTab === 'choices' && (
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="text-lg font-medium">Choice Bets</h4>
                      {selectedSession.active && (
                        <button
                          onClick={() => openBookingModal(selectedSession, 'choice')}
                          className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
                        >
                          Add Choice Bet
                        </button>
                      )}
                    </div>
                    {sessionDetails.choices.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        No choice bets found for this session
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full bg-white">
                          <thead>
                            <tr className="bg-gray-50">
                              <th className="py-2 px-4 border-b text-left">User ID</th>
                              <th className="py-2 px-4 border-b text-left">Numbers</th>
                              <th className="py-2 px-4 border-b text-right">Quantity</th>
                              <th className="py-2 px-4 border-b text-right">Amount</th>
                              <th className="py-2 px-4 border-b text-right">Total</th>
                              <th className="py-2 px-4 border-b text-left">Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sessionDetails.choices.map((choice: any) => (
                              <tr key={choice.id} className="hover:bg-gray-50">
                                <td className="py-2 px-4 border-b">
                                  {choice.user?.userId || 'N/A'}
                                </td>
                                <td className="py-2 px-4 border-b font-mono">
                                  {choice.numbers}
                                </td>
                                <td className="py-2 px-4 border-b text-right">
                                  {choice.qty}
                                </td>
                                <td className="py-2 px-4 border-b text-right">
                                  {formatCurrency(choice.amount)}
                                </td>
                                <td className="py-2 px-4 border-b text-right font-medium">
                                  {formatCurrency(choice.totalAmount)}
                                </td>
                                <td className="py-2 px-4 border-b text-sm text-gray-500">
                                  {new Date(choice.createdAt).toLocaleString()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Results Tab */}
                {activeTab === 'results' && (
                  <div>
                    <h4 className="text-lg font-medium mb-4">Draw Results</h4>
                    {sessionDetails.results.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        No results available for this session
                        {selectedSession.active && (
                          <div className="mt-4">
                            <button
                              onClick={() => conductDraw(selectedSession)}
                              disabled={conductingDraw}
                              className="bg-red-600 text-white px-6 py-2 rounded hover:bg-red-700 disabled:bg-gray-400"
                            >
                              {conductingDraw ? "Conducting Draw..." : "Conduct Draw Now"}
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {sessionDetails.results.map((result: any) => (
                          <div key={result.id} className="bg-gradient-to-br from-yellow-400 to-orange-500 p-6 rounded-lg text-white text-center shadow-lg">
                            <div className="text-sm font-medium mb-2">{result.category} Prize</div>
                            <div className="text-4xl font-bold mb-2 font-mono">
                              {result.number.toString().padStart(3, '0')}
                            </div>
                            <div className="text-sm opacity-90">Winning Number</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Draw Results Modal */}
      {showResultsModal && drawResults && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-8 w-full max-w-2xl">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 mb-4">🎉 Draw Completed Successfully! 🎉</div>
              <p className="text-gray-600 mb-6">
                The draw has been conducted and winnings have been automatically distributed to all winners.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-gradient-to-br from-yellow-400 to-orange-500 p-6 rounded-lg text-white text-center shadow-lg">
                  <div className="text-sm font-medium mb-2">1st Prize</div>
                  <div className="text-3xl font-bold mb-2 font-mono">{drawResults.firstPrize}</div>
                  <div className="text-sm opacity-90">Winning Number</div>
                </div>
                <div className="bg-gradient-to-br from-gray-400 to-gray-600 p-6 rounded-lg text-white text-center shadow-lg">
                  <div className="text-sm font-medium mb-2">2nd Prize</div>
                  <div className="text-3xl font-bold mb-2 font-mono">{drawResults.secondPrize}</div>
                  <div className="text-sm opacity-90">Winning Number</div>
                </div>
                <div className="bg-gradient-to-br from-amber-600 to-amber-800 p-6 rounded-lg text-white text-center shadow-lg">
                  <div className="text-sm font-medium mb-2">3rd Prize</div>
                  <div className="text-3xl font-bold mb-2 font-mono">{drawResults.thirdPrize}</div>
                  <div className="text-sm opacity-90">Winning Number</div>
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="text-green-800 text-sm">
                  <strong>Note:</strong> The session has been closed and no more bets can be placed. 
                  Winnings have been automatically calculated and credited to winners' accounts.
                </div>
              </div>

              <button
                onClick={() => {
                  setShowResultsModal(false);
                  setDrawResults(null);
                }}
                className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}